import CandlestickSettings from './candlestickSettings'
import LineSettingsDialogs from './lineSettingsDialogs'
import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ChartType, chartTypeFromJSON } from '../../proto/generated/chart'
import AreaSettingsDialogs from './areaSettingsDialogs'
import BarSettingsDialogs from './barSettingsDialogs'
import HeikinSettingsDialogs from './heikinSettingsDialogs'
import { updateChartConfig } from '../../reducer/chart.reducer'
import { selectChartConfigSettings, selectChartConfig, selectChartScalesAndLinesSettings, selectChartCanvasSettings, selectChartAlertSettings, selectChartEventsSettings } from '../../selectors/chart'
import { SeriesOptions } from '../chartV2/chart.typings'
import { AppContext } from '../../App'
import { Button, DialogActions, DialogContent, Divider, Typography, Stack } from '@mui/material'
import DraggableDialogTitle from '../common/DraggableDialogTitle'
import { Dialog } from '@mui/material'
import { ExtraSmallButton } from "../custom";
import ClearIcon from "../../assets/icons/ClearIcon";
import HollowCandlestickSettings from './hollowCandlestickSettings'
import HighLowCandlestickSettings from './highLowCandlestickSettings'
import BaselineSettings from './baselineSettings'
import HlcAreaSettings from './hlcAreaSettings'
import RenkoSettings from './renkoSettings'
import KagiSettings from './kagiSettings'
import PandFSettings from './pandFSettings'
import LinkBreakSettings from './lineBreakSettings'
import VolumeCandlesSettings from './volumeCandleSettings'
import StepLineSettings from './stepLineSettings'
import ColumnSettingsDialogs from './columnSettingsDialogs'
import { defaultTradingSettings, defaultAlertSettings } from '../../util/chart'
import { defaultScalesAndLinesSettings } from '../../util/scalesAndLinesDefaults'
import { defaultCanvasSettings } from '../../util/canvasSettingsDefaults'
import { defaultEventsSettings } from '../../util/eventsSettingsDefaults'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { useFullscreenContainer } from '../../hooks/useFullscreenContainer'
import { WidgetSettingsMenuProvider } from '../widgetDashboard/widgetSettingsMenuContext'

const DEFAULT_STATUS_LINE_SETTINGS = {
    logo: true,
    showSymbolName: true,
    showOHLC: true,
    showBarChange: true,
    showLastDayChange: false,
    showExchange: true,
    indicatorTitle: true,
    indicatorInputs: false,
    indicatorValues: true
} as const

type StatusLineSettings = typeof DEFAULT_STATUS_LINE_SETTINGS

const areStatusLineSettingsEqual = (a: StatusLineSettings, b: StatusLineSettings) => (
    a.logo === b.logo &&
    a.showSymbolName === b.showSymbolName &&
    a.showOHLC === b.showOHLC &&
    a.showBarChange === b.showBarChange &&
    a.showLastDayChange === b.showLastDayChange &&
    a.showExchange === b.showExchange &&
    a.indicatorTitle === b.indicatorTitle &&
    a.indicatorInputs === b.indicatorInputs &&
    a.indicatorValues === b.indicatorValues
)

const DEFAULT_SCALES_AND_LINES_SETTINGS = defaultScalesAndLinesSettings

const DEFAULT_CANVAS_SETTINGS = defaultCanvasSettings

interface Props {
    open: boolean;
    onClose: () => void;
    viewKey: string;
    chartType: ChartType;
}

export const DEFAULT_SETTINGS = {
    [ChartType.CANDLESTICK]: {
        visible: true,
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350'
    },
    [ChartType.HLC_AREA]: {
        highLineColor: '#26a69a',
        lowLineColor: '#ef5350',
        closeLineColor: '#2862ff',
        topFillColor: '#00bcd440',
        bottomFillColor: '#e91e6340',
        highLineVisible: true,
        lowLineVisible: false
    },

    [ChartType.STEP_LINE_CHART]: {
        color: '#2962FF'
    },
    [ChartType.HIGH_LOW]: {
        visible: true,
        upColor: '#2862ff',
        downColor: '#2862ff',
        borderVisible: true,
        borderUpColor: '#2862ff',
        borderDownColor: '#2862ff',
        wickVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#26a69a'
    },
    [ChartType.LINE]: {
        color: '#2196f3',
        source: 'close'
    },
    [ChartType.LINE_WITH_MARKERS]: {
        color: '#2196f3',
        source: 'close'
    },
    [ChartType.AREA]: {
        topColor: 'rgba( 46, 220, 135, 0.4)',
        bottomColor: 'rgba( 40, 221, 100, 0)',
        lineColor: '#33D778',
        source: 'close'
    },
    [ChartType.BAR]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        thinBars: false
    },
    [ChartType.LINE_BREAK]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
    },
    [ChartType.VOLUME_CANDLESTICK]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
    },
    [ChartType.HEIKIN_ASHI]: {
        visible: true,
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350'
    },
    [ChartType.HOLLOW_CANDLES]: {
        visible: true,
        upColorHollow: '#26a69a',
        downColorHollow: '#ef5350',
        borderVisibleHollow: false,
        borderUpColorHollow: '#26a69a',
        borderDownColorHollow: '#ef5350',
        wickVisibleHollow: true,
        wickUpColorHollow: '#26a69a',
        wickDownColorHollow: '#ef5350'
    },
    [ChartType.HISTOGRAM]: {
        upColor: '#26a69a80',
        downColor: '#ef535080',
    },
    [ChartType.COLUMN]: {
        upColor: '#26a69a80',
        downColor: '#ef535080',
    },
    [ChartType.BASELINE]: {
        source: "close",
        baseValue: 50,
        topLineColor: '#089981',
        bottomLineColor: '#f23645',
        topFillColor1: '#08998100',
        topFillColor2: '#08998147',
        bottomFillColor1: '#f2364500',
        bottomFillColor2: '#f2364547',
    },
    [ChartType.KAGI]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        projectionUpColor: '#a9dcc3',
        projectionDownColor: '#f5a6ae',
        boxMethod: 'traditional',
        atrLength: 14,
        percentageLTP: 1,
        reversalAmount: 2
    },
    [ChartType.POINT_AND_FIGURE]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        boxMethod: 'atr',
        boxSize: 1,
        atrLength: 14,
        percentage: 1,
        reversalAmount: 3
    },
    [ChartType.RENKO]: {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: true,
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickVisible: true,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
        boxMethod: 'atr',
        boxSize: 10,
        atr: 14,
        percentage: 1,
        projectionUpColor: '#a9dcc3',
        projectionDownColor: '#f5a6ae',
        projectionUpBorderColor: '#a9dcc3',
        projectionDownBorderColor: '#f5a6ae'
    }
} as const

const resolveChartType = (chartType: ChartType | string): ChartType =>
    typeof chartType === 'string' ? chartTypeFromJSON(chartType) : chartType

const chartSettingsComponents = (viewKey: string, chartType: ChartType | string) => {
    switch (resolveChartType(chartType)) {
        case ChartType.CANDLESTICK:
            return <CandlestickSettings viewKey={viewKey} />
        case ChartType.LINE:
            return <LineSettingsDialogs viewKey={viewKey} />
        case ChartType.AREA:
            return <AreaSettingsDialogs viewKey={viewKey} />
        case ChartType.BAR:
            return <BarSettingsDialogs viewKey={viewKey} />
        case ChartType.HEIKIN_ASHI:
            return <HeikinSettingsDialogs viewKey={viewKey} />
        case ChartType.HISTOGRAM:
        case ChartType.COLUMN:
            return <ColumnSettingsDialogs viewKey={viewKey} />
        case ChartType.HOLLOW_CANDLES:
            return <HollowCandlestickSettings viewKey={viewKey} />
        case ChartType.HIGH_LOW:
            return <HighLowCandlestickSettings viewKey={viewKey} />
        case ChartType.BASELINE:
            return <BaselineSettings viewKey={viewKey} />
        case ChartType.HLC_AREA:
            return <HlcAreaSettings viewKey={viewKey} />
        case ChartType.RENKO:
            return <RenkoSettings viewKey={viewKey} />
        case ChartType.KAGI:
            return <KagiSettings viewKey={viewKey} />
        case ChartType.POINT_AND_FIGURE:
            return <PandFSettings viewKey={viewKey} />
        case ChartType.LINE_BREAK:
            return <LinkBreakSettings viewKey={viewKey} />
        case ChartType.VOLUME_CANDLESTICK:
            return <VolumeCandlesSettings viewKey={viewKey} />
        case ChartType.STEP_LINE_CHART:
            return <StepLineSettings viewKey={viewKey} />
        case ChartType.LINE_WITH_MARKERS:
            return <LineSettingsDialogs viewKey={viewKey} />
        default:
            return <></>
    }
}

const ChartSettingsDialogs: React.FC<Props> = ({ open, onClose, viewKey, chartType }) => {
    const dispatch = useDispatch()
    const fullscreenContainer = useFullscreenContainer()
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const container = useSelector(selectMultiPaneFullScreen)
    const getDefaultSettings = (chartType: ChartType) => {
        return DEFAULT_SETTINGS[chartType as keyof typeof DEFAULT_SETTINGS] || {}
    }
    const prevSettingsRef = useRef<SeriesOptions>({})
    const statusLineSettings = (useSelector(selectChartConfigSettings(viewKey)) ?? DEFAULT_STATUS_LINE_SETTINGS) as StatusLineSettings
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const tradingSettings = chartConfig?.tradingSettings || defaultTradingSettings
    const alertSettings = useSelector(selectChartAlertSettings(viewKey)) ?? defaultAlertSettings
    const scalesAndLinesSettings = useSelector(selectChartScalesAndLinesSettings(viewKey)) ?? DEFAULT_SCALES_AND_LINES_SETTINGS
    const canvasSettings = useSelector(selectChartCanvasSettings(viewKey)) ?? DEFAULT_CANVAS_SETTINGS
    const eventsSettings = useSelector(selectChartEventsSettings(viewKey)) ?? defaultEventsSettings
    const [currentStatusLineSettings, setCurrentStatusLineSettings] = useState<StatusLineSettings>(DEFAULT_STATUS_LINE_SETTINGS)
    const [prevStatusLineSettings, setPrevStatusLineSettings] = useState<StatusLineSettings>(DEFAULT_STATUS_LINE_SETTINGS)
    const [currentTradingSettings, setCurrentTradingSettings] = useState(defaultTradingSettings)
    const [prevTradingSettings, setPrevTradingSettings] = useState(defaultTradingSettings)
    const [prevAlertSettings, setPrevAlertSettings] = useState(defaultAlertSettings)
    const [prevScalesAndLinesSettings, setPrevScalesAndLinesSettings] = useState<any>(DEFAULT_SCALES_AND_LINES_SETTINGS)
    const [prevCanvasSettings, setPrevCanvasSettings] = useState<any>(DEFAULT_CANVAS_SETTINGS)
    const [prevEventsSettings, setPrevEventsSettings] = useState(defaultEventsSettings)

    useEffect(() => {
        if (open && chart) {
            const settings = chart.primarySeriesGroup?.getPrimarySeries()?.getDefaultOptions()
            // `priceLineVisible` / `lastValueVisible` are owned by the global scales-and-lines
            // settings (showSymbolLine / showSymbolValue). Don't capture them here, otherwise
            // restoring on cancel would persist them into the per-series options and override
            // the global toggle.
            const { priceLineVisible, lastValueVisible, ...rest } = { ...(settings ?? getDefaultSettings(chartType)) } as SeriesOptions
            prevSettingsRef.current = rest
        }
    }, [open])

    useEffect(() => {
        if (open && chart) {
            const appliedStatusLineSettings = statusLineSettings
            const appliedTradingSettings = tradingSettings
            setCurrentStatusLineSettings((prev) =>
                areStatusLineSettingsEqual(prev, appliedStatusLineSettings) ? prev : (appliedStatusLineSettings as any)
            )
            setPrevStatusLineSettings((prev) =>
                areStatusLineSettingsEqual(prev, appliedStatusLineSettings) ? prev : (appliedStatusLineSettings as any)
            )
            setCurrentTradingSettings((prev) => (prev === appliedTradingSettings ? prev : (appliedTradingSettings as any)))
            setPrevTradingSettings((prev) => (prev === appliedTradingSettings ? prev : (appliedTradingSettings as any)))
            setPrevAlertSettings(alertSettings)
            setPrevScalesAndLinesSettings(scalesAndLinesSettings)
            setPrevCanvasSettings(canvasSettings)
            setPrevEventsSettings({ ...defaultEventsSettings, ...eventsSettings })
        }
    }, [open, chartType, statusLineSettings, tradingSettings, alertSettings, scalesAndLinesSettings, canvasSettings, eventsSettings, chart])

    const handleApplyDefault = () => {
        const defaultSettings = getDefaultSettings(chartType)
        const defaultStatusLineSettings = { ...DEFAULT_STATUS_LINE_SETTINGS } as any
        if (chart) {
            chart.chartConfig = {
                ...chart.chartConfig,
                statusLineSettings: defaultStatusLineSettings,
                tradingSettings: defaultTradingSettings,
                alertSettings: defaultAlertSettings,
                scalesAndLinesSettings: DEFAULT_SCALES_AND_LINES_SETTINGS,
                canvasSettings: DEFAULT_CANVAS_SETTINGS,
                eventsSettings: defaultEventsSettings,
            }
            const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
            if (primarySeries) {
                primarySeries.updateOptions(defaultSettings as SeriesOptions)
            }
        }
        dispatch(updateChartConfig({
            viewKey,
            config: {
                statusLineSettings: defaultStatusLineSettings,
                tradingSettings: defaultTradingSettings,
                alertSettings: defaultAlertSettings,
                scalesAndLinesSettings: DEFAULT_SCALES_AND_LINES_SETTINGS,
                canvasSettings: DEFAULT_CANVAS_SETTINGS,
                eventsSettings: defaultEventsSettings,
            },
            createIfAbsent: false
        }))
        setCurrentStatusLineSettings(defaultStatusLineSettings)
        setPrevStatusLineSettings(defaultStatusLineSettings)
        setCurrentTradingSettings(defaultTradingSettings)
        setPrevTradingSettings(defaultTradingSettings)
        setPrevAlertSettings(defaultAlertSettings)
        setPrevScalesAndLinesSettings(DEFAULT_SCALES_AND_LINES_SETTINGS)
        setPrevCanvasSettings(DEFAULT_CANVAS_SETTINGS)
        setPrevEventsSettings(defaultEventsSettings)
    }

    const handleCancel = () => {
        if (!chart) return
        dispatch(updateChartConfig({
            viewKey,
            config: {
                statusLineSettings: prevStatusLineSettings,
                tradingSettings: prevTradingSettings,
                alertSettings: prevAlertSettings,
                scalesAndLinesSettings: prevScalesAndLinesSettings,
                canvasSettings: prevCanvasSettings,
                eventsSettings: prevEventsSettings,
            },
            createIfAbsent: false
        }))
        if (chart) {
            const primarySeries = chart.primarySeriesGroup?.getPrimarySeries()
            if (primarySeries) {
                primarySeries.updateOptions(prevSettingsRef.current as SeriesOptions)
            }
        }
        setCurrentStatusLineSettings(prevStatusLineSettings)
        setCurrentTradingSettings(prevTradingSettings)
        onClose()
    }

    const handleOk = () => {
        setPrevStatusLineSettings(currentStatusLineSettings)
        setPrevTradingSettings(currentTradingSettings)
        onClose()
    }
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            container={fullscreenContainer ?? undefined}
            hideBackdrop
            disableEnforceFocus
            disableScrollLock
            sx={{ 
                pointerEvents: 'none',
                zIndex: 99999
            }}
            PaperProps={{
                sx: {
                    minHeight: "40%",
                    maxHeight: 'max-content',
                    minWidth: '500px',
                    pointerEvents: 'all',
                }
            }}
        >
            <DraggableDialogTitle>
                <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    <Typography variant={'h6'}>Settings</Typography>
                    <ExtraSmallButton onClick={onClose} aria-label="close" color={"inherit"}>
                        <ClearIcon />
                    </ExtraSmallButton>
                </Stack>
            </DraggableDialogTitle>
            <DialogContent sx={{ padding: "0px", display: "flex", flexDirection: "column", height: '100%' }}>
                <WidgetSettingsMenuProvider>
                    {chartSettingsComponents(viewKey, chartType) || <div>No settings available</div>}
                </WidgetSettingsMenuProvider>
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button variant="outlined" onClick={handleApplyDefault} sx={{ marginRight: 'auto' }}>
                    Apply Default
                </Button>
                <Button variant="outlined" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button onClick={handleOk} variant="contained">
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default memo(ChartSettingsDialogs)